"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Scan } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const workflowSteps = [{
  step: "01",
  title: "Non-Contact Optical Interface",
  description: "Direct optical measurement positioned outside the product stream to maintain process integrity without invasive sampling or physical wear.",
  icon: Scan
}, {
  step: "02",
  title: "Real-Time Spectral Acquisition",
  description: "Continuous data collection during active processing, providing high-frequency analytical measurements as material flows through the system.",
  icon: Activity
}, {
  step: "03",
  title: "Quantitative Homogeneity Determination",
  description: "Algorithmic verification and endpoint monitoring to establish consistent blend uniformity across manufacturing batches.",
  icon: CheckCircle2
}];
export default function Workflow() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (index: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.15,
        ease: "easeOut"
      }
    })
  };
  return <section id="workflowprocess" data-nav="light" className="bg-background py-20 md:py-28">
      <div className="page-container">
        <div className="mx-auto max-w-2xl text-center">
          <Badge variant="secondary" className="mb-4">
            Process Architecture
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            In-Line Analytical Workflow
          </h2>
          <p className="mt-4 font-sans text-base text-muted-foreground sm:text-lg">
            A structured measurement methodology engineered to deliver consistent
            spectral insight across regulated laboratory and manufacturing lines.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
          {workflowSteps.map((step, index) => {
          const IconComponent = step.icon;
          return <motion.div key={step.step} data-index={index} custom={index} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-50px"
          }} variants={cardVariants} className="flex">
                <Card className="relative flex flex-col justify-between overflow-hidden border border-border bg-card shadow-sm transition-[box-shadow,border-color] duration-300 hover:border-border/80 hover:shadow-md">
                  <div aria-hidden="true" className="h-1.5 w-full bg-primary" />
                  <div>
                    <CardHeader className="pt-6">
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-mono text-sm font-semibold tracking-wider text-primary">
                          STEP {step.step}
                        </span>
                        <div className="flex size-10 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                          <IconComponent className="size-5 text-primary" />
                        </div>
                      </div>
                      <CardTitle className="mt-4 font-default text-xl font-semibold text-card-foreground">
                        {step.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="font-sans text-sm leading-relaxed text-muted-foreground">
                        {step.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-2">
                    <div className="h-px w-full bg-border" />
                    <div className="mt-4 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <span className="size-1.5 rounded-full bg-primary" />
                      Standardized protocol
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="mt-14 flex flex-col items-center justify-center gap-4 text-center sm:flex-row">
          <p className="font-sans text-sm text-muted-foreground">
            Looking to integrate analytical PAT methods into your production facility?
          </p>
          <Button asChild variant="default" size="default">
            <Link to="/ContactPage" className="inline-flex items-center gap-2">
              Speak with a Specialist
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}